import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBed } from '../../../context/BedContext';
import { useTeam } from '../../../context/TeamContext';
import { useAuth } from '../../../context/AuthContext';
import { useTCoins } from '../../../context/TCoinsContext';
import { useTeamConfig } from '../../../context/TeamConfigContext';
import { TEAM_CONFIG_PRICING } from '../../../data/defaultTeamConfigs';
import BedCard from './BedCard';
import AddEditBedModal from './AddEditBedModal';
import BedDetailModal from './BedDetailModal';

const BedManagementPage = () => {
  const navigate = useNavigate();
  const { beds, loading, getBedsByStatus, getBedsByWard, createDefaultBedsForTeam, deleteBed } = useBed();
  const { currentTeam, loading: teamLoading } = useTeam();
  const { currentUser } = useAuth();
  const { getTotalTCoins, deductTCoins } = useTCoins();
  const { wards, departments } = useTeamConfig();

  // Debug state - Hidden by default
  const [showDebugPanel, setShowDebugPanel] = useState(false);

  // CRITICAL: Log immediately when component renders
  console.log('═══════════════════════════════════════════════════════');
  console.log('🔍 BedManagementPage - Component Rendered');
  console.log('═══════════════════════════════════════════════════════');
  console.log('📊 STATE VALUES:');
  console.log('  - beds:', beds);
  console.log('  - beds.length:', beds.length);
  console.log('  - beds type:', typeof beds);
  console.log('  - beds is array:', Array.isArray(beds));
  console.log('  - currentTeam:', currentTeam);
  console.log('  - currentTeam?.id:', currentTeam?.id);
  console.log('  - currentTeam?.name:', currentTeam?.name);
  console.log('  - loading:', loading);
  console.log('  - teamLoading:', teamLoading);
  console.log('═══════════════════════════════════════════════════════');

  // UI state
  const [viewMode, setViewMode] = useState('grid');
  const [statusFilter, setStatusFilter] = useState('all');
  const [wardFilter, setWardFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingBed, setEditingBed] = useState(null);
  const [viewingBed, setViewingBed] = useState(null);

  const userPlan = currentUser?.plan || 'FREE';
  const totalTCoins = getTotalTCoins();
  const isLeader = currentTeam?.userRole === 'LEADER' || currentTeam?.userRole === 'DEPUTY_LEADER';

  // Debug logging - Track all state changes
  useEffect(() => {
    console.log('═══════════════════════════════════════════════════════');
    console.log('🔄 BedManagementPage - State Changed (useEffect)');
    console.log('═══════════════════════════════════════════════════════');
    console.log('📊 CURRENT STATE:');
    console.log('  - currentTeam:', currentTeam);
    console.log('  - currentTeam?.id:', currentTeam?.id);
    console.log('  - currentTeam?.name:', currentTeam?.name);
    console.log('  - beds:', beds);
    console.log('  - beds.length:', beds.length);
    console.log('  - beds is array:', Array.isArray(beds));
    if (beds.length > 0) {
      console.log('  - First bed:', beds[0]);
      console.log('  - First bed teamId:', beds[0]?.teamId);
    }
    console.log('  - loading:', loading);
    console.log('  - teamLoading:', teamLoading);
    console.log('═══════════════════════════════════════════════════════');
    console.log('🎯 RENDER DECISION:');
    console.log('  - beds.length > 0:', beds.length > 0);
    console.log('  - Will render beds page:', beds.length > 0);
    console.log('  - Will show loading:', loading || teamLoading);
    console.log('  - Will show "no team":', !currentTeam && beds.length === 0);
    console.log('  - Will show "no permission":', currentTeam && !isLeader);
    console.log('  - Will show "empty state":', currentTeam && beds.length === 0 && !loading && !teamLoading);
    console.log('═══════════════════════════════════════════════════════');
  }, [currentTeam, beds, loading, teamLoading, isLeader]);

  // Filter beds
  const filteredBeds = beds.filter(bed => {
    if (statusFilter !== 'all' && bed.status !== statusFilter) {
      return false;
    }
    if (wardFilter !== 'all' && bed.ward !== wardFilter) {
      return false;
    }
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        bed.bedNumber.toLowerCase().includes(query) ||
        bed.bedName.toLowerCase().includes(query) ||
        bed.ward.toLowerCase().includes(query) ||
        bed.department.toLowerCase().includes(query)
      );
    }
    return true;
  });

  // Get unique wards for filter
  const uniqueWards = [...new Set(beds.map(bed => bed.ward))];

  // Status summary
  const statusSummary = {
    all: beds.length,
    AVAILABLE: getBedsByStatus('AVAILABLE').length,
    IN_USE: getBedsByStatus('IN_USE').length,
    MAINTENANCE: getBedsByStatus('MAINTENANCE').length,
    UNAVAILABLE: getBedsByStatus('UNAVAILABLE').length
  };

  const handleAddBed = () => {
    if (userPlan !== 'PRO' && totalTCoins < TEAM_CONFIG_PRICING.ADD_BED) {
      alert(`คุณต้องมี T Coins อย่างน้อย ${TEAM_CONFIG_PRICING.ADD_BED} เหรียญ หรือสมัคร Pro Plan`);
      return;
    }
    setShowAddModal(true);
  };

  const handleEditBed = (bed) => {
    setEditingBed(bed);
    setShowAddModal(true);
  };

  const handleViewBed = (bed) => {
    setViewingBed(bed);
  };

  const handleDeleteBed = async (bed) => {
    try {
      const result = await deleteBed(bed.id);
      
      if (result.success) {
        alert('ลบเตียงสำเร็จ');
      } else {
        alert(`เกิดข้อผิดพลาด: ${result.error}`);
      }
    } catch (err) {
      console.error('Error deleting bed:', err);
      alert(`เกิดข้อผิดพลาด: ${err.message}`);
    }
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setEditingBed(null);
  };

  const handleCloseDetailModal = () => {
    setViewingBed(null);
  };

  // Debug Panel Component - MUST be defined before any early returns
  const DebugPanel = () => {
    const debugFilteredCount = beds.filter(bed => {
      if (statusFilter !== 'all' && bed.status !== statusFilter) return false;
      if (wardFilter !== 'all' && bed.ward !== wardFilter) return false;
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          bed.bedNumber.toLowerCase().includes(query) ||
          bed.bedName.toLowerCase().includes(query) ||
          bed.ward.toLowerCase().includes(query) ||
          bed.department.toLowerCase().includes(query)
        );
      }
      return true;
    }).length;

    return (
      <div className="bg-yellow-100 border-2 border-yellow-400 rounded-lg p-4 mb-4 text-sm" style={{ zIndex: 9999, position: 'relative' }}>
        <h3 className="font-bold text-yellow-800 mb-2">🔍 Debug Info:</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
          <div>
            <strong>currentTeam:</strong> {currentTeam ? `✅ ${currentTeam.name || currentTeam.id}` : '❌ null'}
          </div>
          <div>
            <strong>beds.length:</strong> <span className={beds.length > 0 ? 'text-green-600 font-bold' : 'text-red-600'}>{beds.length}</span>
          </div>
          <div>
            <strong>loading:</strong> <span className={loading ? 'text-red-600' : 'text-green-600'}>{loading ? '⏳ true' : '✅ false'}</span>
          </div>
          <div>
            <strong>teamLoading:</strong> <span className={teamLoading ? 'text-red-600' : 'text-green-600'}>{teamLoading ? '⏳ true' : '✅ false'}</span>
          </div>
          <div>
            <strong>isLeader:</strong> {isLeader ? '✅ true' : '❌ false'}
          </div>
          <div>
            <strong>Will render beds:</strong> <span className={beds.length > 0 ? 'text-green-600 font-bold' : 'text-red-600'}>{beds.length > 0 ? '✅ YES' : '❌ NO'}</span>
          </div>
          <div>
            <strong>filteredBeds.length:</strong> {debugFilteredCount}
          </div>
          <div>
            <strong>Status:</strong> {beds.length > 0 ? '🟢 HAS BEDS' : !currentTeam && beds.length === 0 && !loading ? '🔴 NO TEAM' : loading || teamLoading ? '⏳ LOADING' : '🟡 EMPTY'}
          </div>
        </div>
        {beds.length > 0 && (
          <div className="mt-2 text-xs">
            <strong>First bed:</strong> {JSON.stringify(beds[0]?.bedNumber || 'N/A')} - {beds[0]?.bedName || 'N/A'}
          </div>
        )}
        {beds.length === 0 && (
          <div className="mt-2 text-xs text-red-600">
            <strong>⚠️ WARNING:</strong> beds.length is 0 but BedContext may have fetched beds. Check console logs.
          </div>
        )}
      </div>
    );
  };

  // Debug button wrapper - Only show if needed (can be toggled via localStorage or removed)
  const DebugButtonWrapper = ({ children }) => {
    // Check if debug mode is enabled via localStorage (for developers)
    const [debugModeEnabled] = useState(() => {
      if (typeof window !== 'undefined') {
        return localStorage.getItem('bedManagementDebug') === 'true';
      }
      return false;
    });

    if (!debugModeEnabled) {
      return <>{children}</>;
    }

    return (
      <>
        <div style={{ position: 'fixed', top: '10px', right: '10px', zIndex: 99999 }}>
          <button
            onClick={() => setShowDebugPanel(!showDebugPanel)}
            className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition font-bold text-lg shadow-lg"
            style={{ zIndex: 100000 }}
          >
            {showDebugPanel ? '🔍 ซ่อน Debug' : '🔍 แสดง Debug'}
          </button>
        </div>
        {children}
      </>
    );
  };

  // CRITICAL: Show beds if we have them, regardless of currentTeam status
  // This is the key fix - beds.length check FIRST
  if (beds.length > 0) {
    console.log('═══════════════════════════════════════════════════════');
    console.log(`✅ BedManagementPage - RENDERING BEDS PAGE`);
    console.log(`   beds.length: ${beds.length}`);
    console.log(`   currentTeam: ${currentTeam ? currentTeam.name : 'null'}`);
    console.log('═══════════════════════════════════════════════════════');
    
    return (
      <DebugButtonWrapper>
        <div className="min-h-screen bg-gray-50 p-4">
          <div className="max-w-7xl mx-auto" style={{ marginTop: '60px' }}>
            {showDebugPanel && <DebugPanel />}

            {/* Header */}
            <div className="mb-6">
              <button
                onClick={() => navigate('/nurse/teams')}
                className="mb-4 text-blue-600 hover:text-blue-700 flex items-center gap-2"
              >
                <span>←</span> กลับ
              </button>
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-bold text-gray-800">จัดการเตียงผู้ป่วย</h1>
                  {currentTeam && (
                    <p className="text-sm text-gray-600 mt-1">ทีม {currentTeam.name}</p>
                  )}
                </div>
                {currentTeam && isLeader && (
                  <button
                    onClick={handleAddBed}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                  >
                    + เพิ่มเตียง
                  </button>
                )}
              </div>
            </div>

            {/* Status Summary */}
            <div className="bg-white rounded-lg shadow-md p-4 mb-6">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-800">{statusSummary.all}</p>
                  <p className="text-xs text-gray-600">ทั้งหมด</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600">{statusSummary.AVAILABLE}</p>
                  <p className="text-xs text-gray-600">🟢 ว่าง</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-red-600">{statusSummary.IN_USE}</p>
                  <p className="text-xs text-gray-600">🔴 ใช้งานอยู่</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-yellow-600">{statusSummary.MAINTENANCE}</p>
                  <p className="text-xs text-gray-600">🟡 ซ่อมบำรุง</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-600">{statusSummary.UNAVAILABLE}</p>
                  <p className="text-xs text-gray-600">⚫ ไม่พร้อมใช้งาน</p>
                </div>
              </div>
            </div>

            {/* Filters & View Mode */}
            <div className="bg-white rounded-lg shadow-md p-4 mb-6">
              <div className="flex flex-col md:flex-row gap-4">
                {/* Search */}
                <div className="flex-1">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="🔍 ค้นหาตามหมายเลขเตียง, ชื่อเตียง, หอผู้ป่วย..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Status Filter */}
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">สถานะ: ทั้งหมด</option>
                  <option value="AVAILABLE">🟢 ว่าง</option>
                  <option value="IN_USE">🔴 ใช้งานอยู่</option>
                  <option value="MAINTENANCE">🟡 ซ่อมบำรุง</option>
                  <option value="UNAVAILABLE">⚫ ไม่พร้อมใช้งาน</option>
                </select>

                {/* Ward Filter */}
                <select
                  value={wardFilter}
                  onChange={(e) => setWardFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">หอผู้ป่วย: ทั้งหมด</option>
                  {uniqueWards.map(ward => (
                    <option key={ward} value={ward}>{ward}</option>
                  ))}
                </select>

                {/* View Mode Toggle */}
                <div className="flex gap-2">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`px-4 py-2 rounded-lg transition ${
                      viewMode === 'grid'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    📊 Grid
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`px-4 py-2 rounded-lg transition ${
                      viewMode === 'list'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    📋 List
                  </button>
                </div>
              </div>
            </div>

            {/* Beds Display */}
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-4 text-gray-600">กำลังโหลด...</p>
              </div>
            ) : filteredBeds.length === 0 ? (
              <div className="bg-white rounded-lg shadow-md p-12 text-center">
                <p className="text-gray-600">ไม่พบเตียงที่ตรงกับเงื่อนไข</p>
              </div>
            ) : (
              <div className={viewMode === 'grid' 
                ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4'
                : 'space-y-4'
              }>
                {filteredBeds.map(bed => (
                  <BedCard
                    key={bed.id}
                    bed={bed}
                    viewMode={viewMode}
                    onEdit={currentTeam && isLeader ? handleEditBed : null}
                    onView={handleViewBed}
                    onDelete={currentTeam && isLeader && !bed.isDefault ? handleDeleteBed : null}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Modals */}
          {showAddModal && (
            <AddEditBedModal
              isOpen={showAddModal}
              bed={editingBed}
              onClose={handleCloseModal}
              wards={wards}
              departments={departments}
              userPlan={userPlan}
              totalTCoins={totalTCoins}
              onSave={() => {
                handleCloseModal();
                // Beds will update automatically via real-time listener
              }}
            />
          )}

          {viewingBed && (
            <BedDetailModal
              isOpen={!!viewingBed}
              bed={viewingBed}
              onClose={handleCloseDetailModal}
              onEdit={currentTeam && isLeader ? () => {
                setViewingBed(null);
                handleEditBed(viewingBed);
              } : null}
            />
          )}
        </div>
      </DebugButtonWrapper>
    );
  }

  // Loading state
  if (loading || teamLoading) {
    console.log('═══════════════════════════════════════════════════════');
    console.log(`⏳ BedManagementPage - RENDERING LOADING STATE`);
    console.log(`   loading: ${loading}`);
    console.log(`   teamLoading: ${teamLoading}`);
    console.log(`   beds.length: ${beds.length}`);
    console.log('═══════════════════════════════════════════════════════');
    return (
      <DebugButtonWrapper>
        <div className="min-h-screen bg-gray-50 p-4">
          <div className="max-w-7xl mx-auto" style={{ marginTop: '60px' }}>
            {showDebugPanel && <DebugPanel />}
            <div className="flex items-center justify-center min-h-[60vh]">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-4 text-gray-600">กำลังโหลดข้อมูล...</p>
              </div>
            </div>
          </div>
        </div>
      </DebugButtonWrapper>
    );
  }

  // No beds and no team
  if (!currentTeam && beds.length === 0) {
    console.log('═══════════════════════════════════════════════════════');
    console.log(`❌ BedManagementPage - RENDERING "NO TEAM" ERROR`);
    console.log(`   currentTeam: ${currentTeam}`);
    console.log(`   beds.length: ${beds.length}`);
    console.log(`   loading: ${loading}`);
    console.log(`   teamLoading: ${teamLoading}`);
    console.log('═══════════════════════════════════════════════════════');
    return (
      <DebugButtonWrapper>
        <div className="min-h-screen bg-gray-50 p-4">
          <div className="max-w-7xl mx-auto" style={{ marginTop: '60px' }}>
            {showDebugPanel && <DebugPanel />}
            <div className="flex items-center justify-center min-h-[60vh]">
              <div className="text-center">
                <p className="text-gray-600 mb-4">กรุณาเลือกทีมก่อน</p>
                <button
                  onClick={() => navigate('/nurse/teams')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  กลับไปหน้าทีม
                </button>
              </div>
            </div>
          </div>
        </div>
      </DebugButtonWrapper>
    );
  }

  // Permission check (only if we have currentTeam)
  if (currentTeam && !isLeader) {
    console.log('═══════════════════════════════════════════════════════');
    console.log(`🚫 BedManagementPage - RENDERING "NO PERMISSION" ERROR`);
    console.log(`   currentTeam: ${currentTeam.name}`);
    console.log(`   isLeader: ${isLeader}`);
    console.log(`   beds.length: ${beds.length}`);
    console.log('═══════════════════════════════════════════════════════');
    return (
      <DebugButtonWrapper>
        <div className="min-h-screen bg-gray-50 p-4">
          <div className="max-w-7xl mx-auto" style={{ marginTop: '60px' }}>
            {showDebugPanel && <DebugPanel />}
            <div className="flex items-center justify-center min-h-[60vh]">
              <div className="text-center">
                <p className="text-gray-600 mb-4">คุณไม่มีสิทธิ์เข้าถึงหน้านี้</p>
                <button
                  onClick={() => navigate('/nurse/teams')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  กลับ
                </button>
              </div>
            </div>
          </div>
        </div>
      </DebugButtonWrapper>
    );
  }

  // Empty state (has team but no beds)
  console.log('═══════════════════════════════════════════════════════');
  console.log(`🟡 BedManagementPage - RENDERING EMPTY STATE`);
  console.log(`   currentTeam: ${currentTeam ? currentTeam.name : 'null'}`);
  console.log(`   beds.length: ${beds.length}`);
  console.log(`   loading: ${loading}`);
  console.log(`   teamLoading: ${teamLoading}`);
  console.log('═══════════════════════════════════════════════════════');
  return (
    <DebugButtonWrapper>
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-7xl mx-auto" style={{ marginTop: '60px' }}>
          {showDebugPanel && <DebugPanel />}

          {/* Header */}
          <div className="mb-6">
            <button
              onClick={() => navigate('/nurse/teams')}
              className="mb-4 text-blue-600 hover:text-blue-700 flex items-center gap-2"
            >
              <span>←</span> กลับ
            </button>
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-800">จัดการเตียงผู้ป่วย</h1>
                {currentTeam && (
                  <p className="text-sm text-gray-600 mt-1">ทีม {currentTeam.name}</p>
                )}
              </div>
            </div>
          </div>

          {/* Empty State */}
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <div className="mb-6">
              <p className="text-lg font-semibold text-gray-800 mb-2">ยังไม่มีเตียงผู้ป่วย</p>
              <p className="text-gray-600 mb-4">คุณสามารถสร้าง default beds (20 เตียง) ได้ทันที</p>
              {currentTeam && isLeader && (
                <button
                  onClick={async () => {
                    if (!window.confirm('สร้าง default beds (20 เตียง) สำหรับทีมนี้หรือไม่?')) {
                      return;
                    }
                    const result = await createDefaultBedsForTeam();
                    if (result.success) {
                      alert(`สร้าง default beds สำเร็จ (${result.count || 20} เตียง)`);
                    } else {
                      alert(`เกิดข้อผิดพลาด: ${result.error}`);
                    }
                  }}
                  className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition"
                >
                  🛏️ สร้าง Default Beds (20 เตียง)
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </DebugButtonWrapper>
  );
};

export default BedManagementPage;
